import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ideIllustration from '@fly/assets/images/ide.svg';
import trayIllustration from '@fly/assets/images/system-tray.svg';
import webIllustration from '@fly/assets/images/web.svg';
import { Text } from '@fly/core';

import SegmentedControl from '../SegmentedControl/SegmentedControl';

type TabId = 'ide' | 'flyweb' | 'tray';

export const TAB_IMAGES: Record<TabId, string> = {
  ide: ideIllustration,
  flyweb: webIllustration,
  tray: trayIllustration,
};

interface TabIllustrationProps {
  readonly activeTab: TabId;
}

function TabIllustration({ activeTab }: TabIllustrationProps) {
  const imagePath = TAB_IMAGES[activeTab];
  return (
    <img
      src={imagePath}
      alt=''
      data-testid={`home-tab-image-${activeTab.toString()}`}
      className='block h-full max-h-[207px] w-full max-w-[702px]'
    />
  );
}

interface TabContentSectionProps {
  readonly tabId: TabId;
}

function TabContentSection({ tabId }: TabContentSectionProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid={`home-tab-text-${tabId}`}
      className='flex flex-col gap-fly-2'
    >
      <h2 className='font-bold text-size-fly-6'>
        {t(`app.home.tabs.${tabId}.title`)}
      </h2>
      <Text size='4' className='text-slateA-11'>
        {t(`app.home.tabs.${tabId}.description`)}
      </Text>
    </div>
  );
}

export default function HomeTabs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('ide');

  const tabs = [
    { value: 'ide' as const, label: t('app.home.tabs.ide.label') },
    { value: 'flyweb' as const, label: t('app.home.tabs.flyweb.label') },
    { value: 'tray' as const, label: t('app.home.tabs.tray.label') },
  ];

  return (
    <div
      data-testid='home-tabs'
      className='lg:max-w-[924px] sm:max-w-[700px] xs:max-w-[500px] mt-[46px] mb-[46px] flex h-full w-full flex-col'
    >
      <div className='bg-slate-4 !pb-0 flex shrink-0 flex-col rounded-t-[32px] p-fly-7'>
        <div className='flex flex-col items-center'>
          <h1
            className='font-semibold mb-fly-6 text-size-fly-4 text-(--text-color)'
            data-testid='home-tabs-title'
          >
            {t('app.home.tabs.section_title')}
          </h1>
          <SegmentedControl<TabId>
            items={tabs}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <div className='mt-10 flex flex-1 items-end justify-center'>
          <div className='flex w-full justify-center overflow-hidden'>
            <TabIllustration activeTab={activeTab} />
          </div>
        </div>
      </div>
      <div className='bg-slate-2 p-8 flex flex-1 flex-col rounded-b-[32px]'>
        <TabContentSection tabId={activeTab} />
      </div>
    </div>
  );
}
